import React from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { Box, Typography, Container, Chip, Paper, Grid, Divider } from '@mui/material';
import { 
  ArrowBack, 
  LocationOn, 
  Phone,
  CalendarToday as CalendarTodayIcon,
  AccessTime as AccessTimeIcon,
  Person as PersonIcon
} from '@mui/icons-material';
import {
  useAppointmentsCreateMutation,
  useAppointmentsRetrieveQuery,
  useAuthProfileRetrieveQuery,
  useSalonsRetrieveQuery,
  useServicesPublicSalonListQuery,
  useTeamsTeamMembersSalonMembersRetrieveQuery,
} from '../../../services/GLOWClient';
import DateTimeSelectionStep from './DateTimeSelectionStep';
import { useSalonPublicTheme } from '../../../context/SalonPublicThemeContext';
import { BASE_URL } from '../../../config';
import Button from '../../common/salonPublicAppearance/SalonPublicButton';

// Helper function to build image URL
const buildImageUrl = (url) => {
  if (!url) return '';
  
  if (/^https?:\/\//i.test(url)) {
    if (url.match(/^http:\/\/localhost\//) && !url.match(/^http:\/\/localhost:[0-9]+\//)) {
      return url.replace('http://localhost/', 'http://localhost:8012/');
    }
    return url;
  }
  
  if (url.startsWith('/media/')) {
    const baseUrl = window.location.hostname === 'localhost' ? 
      'http://localhost:8012' : 
      BASE_URL.replace(/\/$/, '');
    return `${baseUrl}${url}`;
  }
  
  if (url.startsWith('media/')) {
    const baseUrl = window.location.hostname === 'localhost' ? 
      'http://localhost:8012' : 
      BASE_URL.replace(/\/$/, '');
    return `${baseUrl}/${url}`;
  }
  
  return url;
};

const AppointmentRebooking = () => {
  const { tokens: theme } = useSalonPublicTheme();
  const { salonId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const rebookAppointmentId = searchParams.get('rebookAppointment');

  const { data: userProfile } = useAuthProfileRetrieveQuery();
  const [createAppointment, { isLoading: isCreating }] = useAppointmentsCreateMutation();

  // Fetch appointment data for rebooking
  const { data: rebookAppointmentData, isLoading: appointmentLoading, error: appointmentError } = useAppointmentsRetrieveQuery(
    { appointmentId: rebookAppointmentId },
    { skip: !rebookAppointmentId }
  );

  // Fetch salon details
  const { data: salonDetails, isLoading: salonLoading } = useSalonsRetrieveQuery(
    { salonId },
    { skip: !salonId }
  );

  // Fetch services to find the selected service
  const { data: allServices, isLoading: servicesLoading } = useServicesPublicSalonListQuery(
    { salonId },
    { skip: !salonId }
  );

  // Fetch salon members (employees) for DateTimeSelectionStep
  const { data: salonMembersData, isLoading: membersLoading } = useTeamsTeamMembersSalonMembersRetrieveQuery(
    salonId ? { salon: salonId } : {},
    { skip: !salonId }
  );

  // Find the selected service from appointment data
  const selectedService = React.useMemo(() => {
    if (!rebookAppointmentData || !allServices) return null;
    return allServices.find(service => service.service_id === rebookAppointmentData.service);
  }, [rebookAppointmentData, allServices]);

  const handleBack = () => {
    navigate('/client/appointments');
  };

  const handleCreateFromSelection = async ({ date, slot }) => {
    try {
      const selectedTime = typeof slot === 'object' ? slot.start_time : slot;
      const serviceDuration = selectedService?.duration || 60;

      const startDateTime = new Date(`${date}T${selectedTime}:00`);
      const endDateTime = new Date(startDateTime.getTime() + serviceDuration * 60000);

      const formatLocalDateTime = (d) => {
        const year = d.getFullYear();
        const month = String(d.getMonth() + 1).padStart(2, '0');
        const day = String(d.getDate()).padStart(2, '0');
        const hours = String(d.getHours()).padStart(2, '0');
        const minutes = String(d.getMinutes()).padStart(2, '0');
        const seconds = String(d.getSeconds()).padStart(2, '0');
        return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
      };

      await createAppointment({
        appointmentCreateRequest: {
          salon: Number(salonId),
          service: Number(selectedService?.service_id || rebookAppointmentData.service),
          team_member: rebookAppointmentData?.team_member ? Number(rebookAppointmentData.team_member) : null,
          client: Number(userProfile?.id),
          start_time: formatLocalDateTime(startDateTime),
          end_time: formatLocalDateTime(endDateTime),
        },
      }).unwrap();

      navigate('/client/appointments', { replace: true });
      window.location.reload();
    } catch (error) {
      console.error('Failed to rebook appointment:', error);
    }
  };

  if (appointmentLoading || salonLoading || servicesLoading || membersLoading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Typography>Chargement...</Typography>
      </Container>
    );
  }

  if (appointmentError || !rebookAppointmentData) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Box sx={{ mb: 3 }}>
          <Button
            startIcon={<ArrowBack />}
            onClick={handleBack}
            sx={{ mb: 2 }}
          >
            Retour aux rendez-vous
          </Button>
        </Box>
        <Typography color="error">
          Erreur lors du chargement des données du rendez-vous.
        </Typography>
      </Container>
    );
  }

  if (!selectedService) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Box sx={{ mb: 3 }}>
          <Button
            startIcon={<ArrowBack />}
            onClick={handleBack}
            sx={{ mb: 2 }}
          >
            Retour aux rendez-vous
          </Button>
        </Box>
        <Typography color="error">
          Service non trouvé pour ce rendez-vous.
        </Typography>
      </Container>
    );
  }

  return (
    <Container
      maxWidth="xl"
      style={{
        backgroundColor: theme.salon.background,
        fontFamily: theme.typography.fontFamily.sans.join(', '),
      }}
      sx={{ py: 4 }}
    >
      <Paper sx={{ mb: 4, overflow: 'hidden' }}>
        {/* Header with Orange Gradient */}
        <Box sx={{ 
          background: `linear-gradient(135deg, ${theme.colors.secondary[500]} 0%, ${theme.colors.secondary[600]} 100%)`,
          p: 4,
          color: theme.salon.compatibilityMode ? 'white' : theme.salon.onSecondary,
          position: 'relative'
        }}>
          {/* Back Button */}
          <Button 
            startIcon={<ArrowBack />}
            onClick={handleBack}
            sx={{ 
              position: 'absolute',
              top: { xs: 12, sm: 24 },
              left: { xs: 12, sm: 24 },
              color: 'white',
              fontWeight: 600,
              backgroundColor: 'rgba(255, 255, 255, 0.2)',
              backdropFilter: 'blur(10px)',
              px: { xs: 2, sm: 3 },
              py: { xs: 0.75, sm: 1 },
              borderRadius: 2,
              fontSize: { xs: '0.875rem', sm: '1rem' },
              minWidth: 'auto',
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.3)',
                transform: 'translateX(-4px)',
                transition: 'all 0.2s'
              }
            }}
          >
            Retour
          </Button>

          {/* Title */}
          <Typography variant="h4" sx={{ 
            fontWeight: 700, 
            mb: 3, 
            textShadow: '0 2px 4px rgba(0,0,0,0.2)',
            textAlign: 'center',
            pt: { xs: 4, sm: 0 }
          }}>
            Reprendre un rendez-vous
          </Typography>
          
          {/* Salon Info */}
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 600, mb: 1 }}>
              {salonDetails?.name}
            </Typography>
            {salonDetails?.address && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, opacity: 0.95 }}>
                <LocationOn sx={{ fontSize: '1.1rem' }} />
                <Typography variant="body2">
                  {salonDetails.address}
                </Typography>
              </Box>
            )}
          </Box>
        </Box>

        {/* Previous Appointment Info */}
        <Box sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="h6" sx={{ fontWeight: 600, color: theme.colors.secondary[700] }}>
              Informations du rendez-vous précédent
            </Typography>
          </Box>
          
          <Box sx={{ 
            p: 3,
            backgroundColor: theme.colors.secondary[50],
            borderRadius: 2,
            border: `1px solid ${theme.colors.secondary[200]}`,
            mb: 3
          }}>
            {/* Date, Heure, Durée - Centrés */}
            <Box sx={{ display: 'flex', justifyContent: 'center', gap: 6, mb: 3, flexWrap: 'wrap' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CalendarTodayIcon sx={{ fontSize: '1.2rem', color: theme.colors.secondary[600] }} />
                <Box>
                  <Typography variant="caption" sx={{ color: theme.colors.text.tertiary, display: 'block', fontSize: '0.7rem' }}>
                    Date
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600, color: theme.colors.text.primary, fontSize: '0.875rem' }}>
                    {new Date(rebookAppointmentData.start_time).toLocaleDateString('fr-FR', { 
                      day: 'numeric', 
                      month: 'long',
                      year: 'numeric'
                    })}
                  </Typography>
                </Box>
              </Box>
              
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <AccessTimeIcon sx={{ fontSize: '1.2rem', color: theme.colors.secondary[600] }} />
                <Box>
                  <Typography variant="caption" sx={{ color: theme.colors.text.tertiary, display: 'block', fontSize: '0.7rem' }}>
                    Heure
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600, color: theme.colors.text.primary, fontSize: '0.875rem' }}>
                    {new Date(rebookAppointmentData.start_time).toLocaleTimeString('fr-FR', { 
                      hour: '2-digit', 
                      minute: '2-digit' 
                    })}
                  </Typography>
                </Box>
              </Box>
              
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <AccessTimeIcon sx={{ fontSize: '1.2rem', color: theme.colors.secondary[600] }} />
                <Box>
                  <Typography variant="caption" sx={{ color: theme.colors.text.tertiary, display: 'block', fontSize: '0.7rem' }}>
                    Durée
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600, color: theme.colors.text.primary, fontSize: '0.875rem' }}>
                    {selectedService?.formatted_duration || `${Math.round((new Date(rebookAppointmentData.end_time) - new Date(rebookAppointmentData.start_time)) / 60000)} min`}
                  </Typography>
                </Box>
              </Box>
            </Box>

            <Divider sx={{ mb: 3 }} />

            {/* Services Table */}
            <Box sx={{ 
              backgroundColor: 'white',
              borderRadius: 2,
              overflow: 'hidden'
            }}>
              {/* Table Header */}
              <Box sx={{ 
                display: 'grid',
                gridTemplateColumns: '2fr 1fr 1fr',
                gap: 2,
                p: 2,
                backgroundColor: theme.colors.secondary[100],
                fontWeight: 600
              }}>
                <Typography variant="body2" sx={{ fontWeight: 600, color: theme.colors.secondary[800] }}>
                  Prestation
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 600, color: theme.colors.secondary[800], textAlign: 'center' }}>
                  Durée
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 600, color: theme.colors.secondary[800], textAlign: 'right' }}>
                  Prix
                </Typography>
              </Box>

              {/* Table Row */}
              <Box sx={{ 
                display: 'grid',
                gridTemplateColumns: '2fr 1fr 1fr',
                gap: 2,
                p: 2,
                borderBottom: `1px solid ${theme.colors.secondary[100]}`
              }}>
                <Typography variant="body2" sx={{ color: theme.colors.text.primary, fontWeight: 500 }}>
                  {selectedService?.name || rebookAppointmentData.service_name}
                </Typography>
                <Typography variant="body2" sx={{ color: theme.colors.text.secondary, textAlign: 'center' }}>
                  {selectedService?.formatted_duration || `${Math.round((new Date(rebookAppointmentData.end_time) - new Date(rebookAppointmentData.start_time)) / 60000)} min`}
                </Typography>
                <Typography variant="body2" sx={{ color: theme.colors.secondary[600], fontWeight: 700, textAlign: 'right' }}>
                  {selectedService?.price || rebookAppointmentData.service_price} DA
                </Typography>
              </Box>

              {/* Team Member */}
              {rebookAppointmentData.team_member_name && (
                <Box sx={{ p: 2, backgroundColor: theme.colors.secondary[50] }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <PersonIcon sx={{ fontSize: '1rem', color: theme.colors.secondary[600] }} />
                    <Typography variant="body2" sx={{ color: theme.colors.text.secondary, fontWeight: 500 }}>
                      Avec {rebookAppointmentData.team_member_name}
                    </Typography>
                  </Box>
                </Box>
              )}
            </Box>
          </Box>
        </Box>
      </Paper>

      {isCreating && (
        <Box sx={{ mb: 3 }}>
          <Typography sx={{ color: theme.colors.text.secondary }}>Création du rendez-vous...</Typography>
        </Box>
      )}

      {/* Date/Time Selection (same UI as booking) */}
      <DateTimeSelectionStep
        salonId={salonId}
        salonDetails={salonDetails}
        employees={Array.isArray(salonMembersData) ? salonMembersData : (salonMembersData?.results || [])}
        selectedServices={[{
          service_id: selectedService?.service_id,
          duration: selectedService?.duration,
          price: selectedService?.price,
          teamMember: rebookAppointmentData?.team_member || 'any'
        }]}
        showBackButton={false}
        onBack={handleBack}
        onCancel={handleBack}
        onContinue={handleCreateFromSelection}
      />
    </Container>
  );
};

export default AppointmentRebooking;
